import React, { useState } from 'react';
import { submitJobDescriptionFeedback } from './api';
import type { DideWriterEndpointBase } from './endpoints';

interface FeedbackBarProps {
  draftId: number;
  endpointBase?: DideWriterEndpointBase;
}

const STARS = [1, 2, 3, 4, 5] as const;

export const FeedbackBar: React.FC<FeedbackBarProps> = ({ draftId, endpointBase }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const send = async () => {
    if (!rating) return;
    setState('sending');
    setErrorMsg(null);
    const result = await submitJobDescriptionFeedback(
      draftId,
      { rating, comment: comment.trim() || undefined },
      { endpointBase }
    );
    if (result.success) {
      setState('sent');
    } else {
      setState('error');
      setErrorMsg(result.error ?? 'Could not send feedback. Please try again.');
    }
  };

  if (state === 'sent') {
    return (
      <div
        role="status"
        className="alert alert-success py-2 px-3 mb-0 small d-flex justify-content-between align-items-center gap-2"
        aria-live="polite"
      >
        <span>Thanks for the feedback.</span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => setState('idle')}
          data-testid="feedback-update"
        >
          Update rating
        </button>
      </div>
    );
  }

  // Just the star row until a rating is picked, so feedback doesn't crowd
  // the draft.
  return (
    <div className="border-top pt-2">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="small text-muted me-1">How is this draft?</span>
        <div role="radiogroup" aria-label="Rate this draft" className="d-flex gap-1">
          {STARS.map(value => {
            const active = rating !== null && value <= rating;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={rating === value}
                aria-label={`${value} star${value === 1 ? '' : 's'}`}
                className={`btn btn-sm py-0 px-1 border-0 ${active ? 'text-warning' : 'text-secondary'}`}
                style={{ fontSize: '1.1rem', lineHeight: 1.2 }}
                disabled={state === 'sending'}
                onClick={() => setRating(value)}
              >
                {active ? '★' : '☆'}
              </button>
            );
          })}
        </div>
      </div>

      {rating !== null && (
        <div className="d-flex flex-wrap align-items-start gap-2 mt-2">
          <input
            type="text"
            className="form-control form-control-sm flex-grow-1"
            style={{ minWidth: 180, flexBasis: 0 }}
            maxLength={1000}
            placeholder="Optional: anything specific?"
            aria-label="Feedback comment"
            value={comment}
            disabled={state === 'sending'}
            onChange={e => setComment(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={state === 'sending'}
            onClick={send}
          >
            {state === 'sending' ? 'Sending…' : 'Send feedback'}
          </button>
          {errorMsg && <span className="small text-danger w-100">{errorMsg}</span>}
        </div>
      )}
    </div>
  );
};

export default FeedbackBar;
